// CredentialsResponse
import type { CredentialsResponse } from "../types/CredentialsReponse";
import axios from "../core/axios";
import type APIResponse from "../types/response.type";
import type AccountDTO from "../types/AccountDTO";
import type OrganizationDto from "../types/OrganizationDTO";
import {
  ApiResponse,
  CreateOrganizationRequest,
  Organization,
} from "../types/OrganizationDTO";
import type { BootstrapResponse } from "../types/BootstrapDTO";

// import keycloak from "../core/keycloak";

export class AccountService {
  async getCredentials(): Promise<APIResponse<CredentialsResponse>> {
    const response = await axios.get<APIResponse<CredentialsResponse>>(
      "/organizations/credentials",
    );

    if (response.status === 200) {
      return response.data;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw "Unable to fetch credentials";
    }
  }

  async regenerateCredentials(): Promise<APIResponse<CredentialsResponse>> {
    const response = await axios.get<APIResponse<CredentialsResponse>>(
      "/organizations/credentials/regenerate",
    );

    if (response.status === 200) {
      return response.data;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw "Unable to regenerate credentials";
    }
  }

  async createOrganization(
    data: CreateOrganizationRequest,
  ): Promise<APIResponse<Organization>> {
    const response = await axios.post<APIResponse<Organization>>(
      "/organizations",
      data,
    );
    return response.data;
  }

  async fetchUserOrganizations(): Promise<Organization[]> {
    const response =
      await axios.get<ApiResponse<Organization[]>>("/organizations");
    return response.data.data || [];
  }

  async fetchOrganizationById(id: string): Promise<Organization> {
    const response = await axios.get<Organization>(`/organizations/${id}`);
    return response.data;
  }

  async updateOrganization(
    id: string,
    data: Partial<Organization>,
  ): Promise<Organization> {
    const response = await axios.patch<Organization>(
      `/organizations/${id}`,
      data,
    );
    return response.data;
  }

  async deleteOrganization(id: string): Promise<void> {
    const response = await axios.delete(`/organizations/${id}`);
    if (response.status !== 200 && response.status !== 204) {
      throw new Error("Unable to delete organization");
    }
  }

  async getBillingInfo(id: string): Promise<any> {
    const response = await axios.get(`/organizations/${id}/billing`);
    return response.data;
  }

  /**
   * Get current user's membership role for a specific organization
   */
  async getCurrentUserMembershipRole(organizationId: string, userId: string): Promise<string | null> {
    try {
      const response = await axios.get<any>(`/organizations/user/${userId}/memberships`);
      const body = response.data;
      const memberships: any[] = Array.isArray(body) ? body : (body?.data ?? []);
      const membership = memberships.find((m: any) => m.organizationId === organizationId);
      return membership?.role || null;
    } catch (error) {
      console.error("Failed to fetch user membership role:", error);
      return null;
    }
  }

  /**
   * Get bootstrap data for session initialization
   * Consolidates multiple API calls into one to reduce frontend load times
   *
   * Replaces separate calls to:
   * - GET /organizations (list user orgs)
   * - GET /organizations/user/:userId/memberships
   * - GET /organizations/:id/payment-status
   */
  async getBootstrapData(): Promise<BootstrapResponse> {
    const response = await axios.get<ApiResponse<BootstrapResponse>>("/organizations/bootstrap");
    return response.data.data;
  }
}
